import { config } from "./config";
import { NewEditSuggestion } from "./EditSuggestion";
import { NameSearchResult, NewAPIResponse } from "./ResultTypes";
import { CreateUser, User } from "./UserType";

const api_path =
  window.location.hostname === "localhost" ? "http://localhost:3001" : "/api";

export function getSession() {
  return fetch(`${api_path}/init`, {
    credentials: "include",
  });
}

function makeGet(url: string) {
  return fetch(url, {
    credentials: "include",
  });
}

function makePost(url: string, body?: any) {
  return fetch(url, {
    method: "POST",
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  });
}

function makeAdminGet(url: string, jwt: string) {
  return fetch(url, {
    method: "GET",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwt}`,
    },
  });
}

function makeAdminPost(url: string, jwt: string, data?: any) {
  return fetch(url, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwt}`,
    },
    body: data ? JSON.stringify(data) : undefined,
  });
}

function makeAdminDelete(url: string, jwt: string, data?: any) {
  return fetch(url, {
    method: "DELETE",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwt}`,
    },
    body: data ? JSON.stringify(data) : undefined,
  });
}

export function getAddresses(
  address_1: string,
  city?: string,
  zip_code?: string,
  coordinates?: number[]
) {
  return makeGet(
    `${api_path}/search-address?address_1=${address_1.replaceAll("#", "%23")}${
      city ? `&city=${city}` : ""
    }
    ${zip_code ? `&zip_code=${zip_code}` : ""}
    ${coordinates ? `&coordinates=${coordinates}` : ""}`
  ).then((data) => {
    return data.json();
  });
}

export function getPropertyById(propertyId: string) {
  return makeGet(`${api_path}/get-property?id=${propertyId}`).then((data) => {
    return data.json().then((json: NewAPIResponse) => {
      return json;
    });
  });
}

export function findByName(name: string) {
  return makeGet(`${api_path}/find?name=${name}`).then((data) => {
    return data.json().then((json: NameSearchResult) => {
      return json;
    });
  });
}

export function findPropertyManager(name: string) {
  return makeGet(`${api_path}/property_manager?name=${name}`).then((data) => {
    return data.json().then((json: NameSearchResult) => {
      return json;
    });
  });
}

export function submitResponse(submission: any) {
  return makePost(`${api_path}/submit-review`, submission);
}

export function submitContact(contact: {
  name: string;
  email: string;
  sourceSystem: string;
  property_id?: string;
  skills?: string[];
}) {
  return makePost(`${api_path}/submit-contact`, contact);
}

export function submitReport(report: any) {
  return makePost(`${api_path}/submit-report`, report);
}

export function reportVacancy(vacancy: any) {
  return makePost(`${api_path}/submit-vacancy`, vacancy);
}

export function admin(jwt: string) {
  return makeAdminGet(`${api_path}/admin`, jwt).then((response) =>
    response.json()
  );
}

export function determine(
  jwt: string,
  id: number,
  approve: string,
  comment: string | null = null
) {
  return makeAdminPost(
    `${api_path}/determine?id=${id}&approve=${approve}`,
    jwt,
    { comment }
  );
}

export function resolveReport(jwt: string, id: number) {
  return makeAdminPost(`${api_path}/resolve?id=${id}`, jwt);
}

export function getAllMapObjects() {
  return makeGet(
    `${api_path}/all-map-objects?source-system=${config.CITY}`
  ).then((response) => response.json());
}

export function login(token: string) {
  return makePost(`${api_path}/auth/login`, {
    token: token,
  }).then((response) => response.json());
}

export function loginPassword(username: string, password: string) {
  return makePost(`${api_path}/auth/login/password`, {
    username: username,
    password: password,
  }).then((response) => response.json());
}

export function getAllUsers(jwt: string): Promise<User[]> {
  return makeAdminGet(`${api_path}/users/all`, jwt).then((response) =>
    response.json()
  );
}

export function updateUser(jwt: string, user: User) {
  return makeAdminPost(`${api_path}/users/update`, jwt, { user }).then(
    (response) => response.json()
  );
}

export function createUser(jwt: string, user: CreateUser): Promise<User[]> {
  return makeAdminPost(`${api_path}/users/create`, jwt, { user }).then(
    (response) => response.json()
  );
}

export function deleteUser(jwt: string, userId: number): Promise<User[]> {
  return makeAdminDelete(`${api_path}/users/delete`, jwt, { userId }).then(
    (response) => response.json()
  );
}

export function ignoreContact(jwt: string, email: string, ignore: boolean) {
  return makeAdminPost(
    `${api_path}/admin/ignore_contact?ignore=${
      ignore ? "yes" : "no"
    }&email=${email}`,
    jwt
  );
}

export function addEditSuggestion(edit: NewEditSuggestion) {
  return makePost(`${api_path}/add-edit-suggestion`, edit);
}

export function voteOnEdit(editId: string, vote: -1 | 1) {
  return makePost(`${api_path}/vote-on-edit?edit-id=${editId}&vote=${vote}`);
}

export function approveSuggestion(jwt: string, id: number, approve: string) {
  return makeAdminPost(
    `${api_path}/approve-suggestion?id=${id}&approve=${approve}`,
    jwt
  );
}

export function approveVacancy(jwt: string, id: number, approve: string) {
  return makeAdminPost(
    `${api_path}/approve-vacancy?id=${id}&approve=${approve}`,
    jwt
  );
}

export function submitSuggestion(suggestion: any) {
  return makePost(`${api_path}/submit-suggestion`, suggestion);
}

export function voteOnSuggestionPost(suggestionId: string, vote: number) {
  return makePost(
    `${api_path}/vote-on-suggestion?id=${suggestionId}&vote=${vote}&source-system=${config.CITY}`
  );
}

export function testDiscord(token: string) {
  return makeAdminPost(`${api_path}/test-discord`, token);
}
